import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '@/lib/utils'
import { GameBoard } from './components/GameBoard'
import { GameFooter } from './components/GameFooter'
import { CardInspectorOverlay } from './components/CardInspectorOverlay'
import { CardCatalog } from './components/CardCatalog'
import { GameResultOverlay } from './components/GameResultOverlay'
import type { CardData, Keyword } from './types'
import './styles/styles.css'
import {
  XStateGameEngine,
  AIDecisionEngine,
  type GameState,
  LANE_SIZE,
  type BoardCreature as SharedBoardCreature,
  STARTING_HEALTH,
} from '@buff/game-engine'
import { Toaster, toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ScrollArea } from '@/components/ui/scroll-area'
import { CardInspector } from './components/CardInspector'
import { Embers } from './components/Embers'
import { Rain } from './components/Rain'
import { FilmEffect } from './components/FilmEffect'
import { highElfMageDeck, noirDetectiveDeck, nordWarriorDeck, sunsetDetectiveDeck } from './data/cards'

const STARTING_CARDS = 4
const MAX_HAND_SIZE = 10

// Local types for UI
interface BoardCreature {
  id: string
  card: CardData
  currentPower: number
  currentHealth: number
  keywords: Keyword[]
  canAttack: boolean
  hasAttacked: boolean
  ward: boolean
  owner: string
  equippedItems: CardData[]
}

interface PlayerState {
  id: string
  name: string
  health: number
  magicka: number
  maxMagicka: number
  hand: CardData[]
  deck: CardData[]
  runes: number[]
}

interface GameClientState {
  players: PlayerState[]
  currentPlayerIndex: number
  turn: number
  phase: 'playing' | 'ended'
  board: {
    playerLane: (BoardCreature | null)[]
    opponentLane: (BoardCreature | null)[]
  }
  winner: string | null
}

interface GameEvent {
  id: number
  turn: number
  message: string
  timestamp: Date
}

// Shared cards for special effects
const _CLUE_CARD: CardData = {
  id: 'clue-token',
  name: 'Collected Clue',
  type: 'action',
  cost: 1,
  power: null,
  health: null,
  description: 'Evidence revealed.',
  keywords: [],
  imageUrl: '/cards/noir/clue_token.png',
}

const _HITMAN_CARD: CardData = {
  id: 'ms-hitman',
  name: 'Syndicate Hitman',
  type: 'creature',
  cost: 1,
  power: 1,
  health: 1,
  description: 'Contract fulfilled.',
  keywords: ['lethal', 'charge'],
  imageUrl: '/cards/noir/ms-12_v3.png',
}

function shuffleDeck(deck: CardData[]): CardData[] {
  const shuffled = [...deck]
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
      ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
  }
  return shuffled
}


import { Scanlines } from './components/Scanlines'

function ThemeSelection({ onSelect, onViewCatalog }: { onSelect: (theme: 'FANTASY' | 'NOIR' | 'SUNSET') => void, onViewCatalog: () => void }) {
  const [hoveredTheme, setHoveredTheme] = useState<'FANTASY' | 'NOIR' | 'SUNSET' | null>(null)

  const themes = [
    {
      id: 'FANTASY' as const,
      name: 'THE BYLINA OF BONES',
      archetype: 'Midrange / Resilience',
      color: 'emerald',
      description: 'Ancient spirits and steel-clad bogatyrs. Dominate through ancestral power.',
      bg: '/cards/fantasy/lobby_f-1.png'
    },
    {
      id: 'NOIR' as const,
      name: 'MIDNIGHT SYNDICATE',
      archetype: 'Control / Removal',
      color: 'zinc',
      description: 'Tactical removal and info gathering. Master of lethality and clues.',
      bg: '/backgrounds/noir.png'
    },
    {
      id: 'SUNSET' as const,
      name: 'SUNSET INVESTIGATIONS',
      archetype: 'Aggro / Speed',
      color: 'violet',
      description: 'Fast-paced offense and aggressive combos. Win before they see you.',
      bg: '/backgrounds/sunset.png'
    }
  ]

  const activeTheme = hoveredTheme || 'FANTASY'

  return (
    <div className="fixed inset-0 flex flex-col items-center justify-center bg-black text-white overflow-hidden select-none">
      {/* Immersive Background Transition */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTheme}
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 0.4, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.5, ease: "easeOut" }}
          className="absolute inset-0 z-0"
        >
          <img
            src={`/backgrounds/${activeTheme.toLowerCase()}.png`}
            className="w-full h-full object-cover grayscale-[0.2]"
            alt=""
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/80" />
        </motion.div>
      </AnimatePresence>

      <div className="absolute inset-0 pointer-events-none z-[1]">
        {activeTheme === 'FANTASY' && <Embers />}
        {activeTheme === 'NOIR' && <Rain />}
        {activeTheme === 'SUNSET' && <Scanlines />}
      </div>

      <header className="absolute top-0 left-0 h-14 w-full px-6 flex items-center z-[110] border-b border-white/5 bg-black/20 backdrop-blur-sm">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="flex items-center gap-3"
        >
          <h1 className="text-3xl font-action tracking-wider leading-none text-white uppercase mt-1 drop-shadow-lg">
            BUFF
          </h1>
          <span className="px-1.5 py-0.5 mt-1 bg-white/10 border border-white/10 rounded-[2px] text-[8px] font-black tracking-[0.2em] text-white/60 uppercase">
            ALPHA
          </span>
        </motion.div>

        <div className="flex-1" />

        <div className="flex items-center gap-8">
          <button
            onClick={onViewCatalog}
            className="text-[10px] font-black tracking-[0.4em] text-white/40 hover:text-white transition-colors uppercase"
          >
            View Catalog
          </button>
          <a
            href="https://buymeacoffee.com/chrctr"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[9px] font-black tracking-[0.2em] text-white/20 hover:text-white/60 transition-colors uppercase"
          >
            Buy me a coffee
          </a>
        </div>
      </header>

      {/* Faction Cards */}
      <div className="flex gap-8 z-10 p-8 pt-20">
        {themes.map((t, idx) => (
          <motion.button
            key={t.id}
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 * idx }}
            onMouseEnter={() => setHoveredTheme(t.id)}
            onMouseLeave={() => setHoveredTheme(null)}
            onClick={() => onSelect(t.id)}
            className={cn(
              "group relative w-[320px] h-[460px] bg-zinc-900 overflow-hidden border border-white/5 transition-all duration-500",
              "hover:border-white/20 hover:scale-[1.02] hover:-y-2 shadow-2xl"
            )}
          >
            {/* Card BG with zoom effect */}
            <div className="absolute inset-0 z-0">
              <img
                src={t.bg}
                className={cn(
                  "w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110 opacity-30 group-hover:opacity-60",
                  t.id === 'NOIR' && "object-[50%_15%]"
                )}
                alt=""
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
            </div>

            {/* Content */}
            <div className="relative h-full flex flex-col justify-end p-8 z-10 text-left">
              <div className="space-y-4">
                <div>
                  <h3 className="text-3xl font-theme font-black tracking-tight mb-1 group-hover:text-theme-primary transition-colors italic">
                    {t.name}
                  </h3>
                  <p className="text-[10px] font-black tracking-widest text-white/40 uppercase">
                    {t.archetype}
                  </p>
                </div>

                <p className="text-[11px] leading-relaxed text-white/60 font-medium">
                  {t.description}
                </p>
              </div>
            </div>

            {/* Scanline Effect on card */}
            <div className="absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-10 pointer-events-none" />
          </motion.button>
        ))}
      </div>

      <div className="absolute bottom-12 w-full px-12 flex flex-col items-center gap-4 z-10">
        <div className="text-[8px] font-black tracking-[0.6em] text-white/20 uppercase">
          2026 Character Development
        </div>
      </div>
    </div>
  )
}

function App() {
  const [appState, setAppState] = useState<'MENU' | 'GAME'>('MENU')
  const [selectedTheme, setSelectedTheme] = useState<'FANTASY' | 'NOIR' | 'SUNSET'>('FANTASY')
  const [gameEngine, setGameEngine] = useState<XStateGameEngine | null>(null)
  const [gameState, setGameState] = useState<GameClientState | null>(null)
  const [turnTimeRemaining, setTurnTimeRemaining] = useState(30)
  const [turnTimerConfig] = useState(30) // Configurable turn timer in seconds

  const [playerId] = useState('player-1')
  const [selectedCreatureId, setSelectedCreatureId] = useState<string | null>(null)
  const [inspectedCardId, setInspectedCardId] = useState<string | null>(null)
  const [gameResult, setGameResult] = useState<'win' | 'lose' | null>(null)
  const [events, setEvents] = useState<GameEvent[]>([])
  const [_showTurnNotification, _setShowTurnNotification] = useState(false)
  const [dragOverSlot, setDragOverSlot] = useState<number | null>(null)
  const [showCatalog, setShowCatalog] = useState(false)

  const eventIdRef = useRef(0)
  const _eventLogRef = useRef<HTMLDivElement>(null)
  const turnRef = useRef(1)

  const isNoir = selectedTheme === 'NOIR'
  const isSunset = selectedTheme === 'SUNSET'

  const addEvent = useCallback((message: string) => {
    const event: GameEvent = {
      id: eventIdRef.current++,
      turn: turnRef.current,
      message,
      timestamp: new Date(),
    }
    setEvents((prev) => [event, ...prev].slice(0, 100))
  }, [])

  // Convert shared GameEngine state to client state
  const convertGameState = useCallback((engineState: GameState): GameClientState => {
    return {
      players: engineState.players.map(p => ({
        ...p,
        hand: p.hand.slice(0, MAX_HAND_SIZE)
      })),
      currentPlayerIndex: engineState.currentPlayerIndex,
      turn: engineState.turn,
      phase: engineState.phase as 'playing' | 'ended',
      board: {
        playerLane: engineState.board.playerLane,
        opponentLane: engineState.board.opponentLane,
      },
      winner: engineState.winner,
    }
  }, [])

  const initializeGame = useCallback(() => {
    console.log('[initializeGame] Starting with theme:', selectedTheme)

    let player1Deck: CardData[] = []
    let player2Deck: CardData[] = []

    if (selectedTheme === 'NOIR') {
      player1Deck = shuffleDeck([...noirDetectiveDeck])
      player2Deck = shuffleDeck([...noirDetectiveDeck])
    } else if (selectedTheme === 'SUNSET') {
      player1Deck = shuffleDeck([...sunsetDetectiveDeck])
      player2Deck = shuffleDeck([...sunsetDetectiveDeck])
    } else {
      player1Deck = shuffleDeck([...highElfMageDeck])
      player2Deck = shuffleDeck([...nordWarriorDeck])
    }

    // Create game engine with both players (AI is player 2)
    const engine = new XStateGameEngine()

    engine.startGame(
      { id: 'player-1', name: 'Player', deck: player1Deck, type: 'human' },
      { id: 'player-2', name: 'Opponent', deck: player2Deck, type: 'ai' }
    )

    setGameEngine(engine)
    setGameState(convertGameState(engine.getGameState()))
    setGameResult(null)
    setSelectedCreatureId(null)
    setInspectedCardId(null)
    setEvents([])
    addEvent('Game started')
    addEvent(`Initial hand: ${STARTING_CARDS} cards`)
  }, [addEvent, selectedTheme, convertGameState])

  useEffect(() => {
    if (appState === 'GAME' && !gameEngine) {
      initializeGame()
    }
  }, [appState, gameEngine, initializeGame])

  const currentPlayerIndex = gameState?.currentPlayerIndex ?? 0
  const currentPlayer = gameState?.players[currentPlayerIndex]
  const isMyTurn = gameState?.players[currentPlayerIndex]?.id === playerId

  // Execute AI turns automatically
  const executeAITurns = useCallback(() => {
    if (!gameEngine) return

    const ai = new AIDecisionEngine()
    let gameState = gameEngine.getGameState()
    let actionCount = 0
    const maxActions = 100 // Prevent infinite loops

    // Execute one AI action per game tick to allow UI updates
    const executeOneAction = () => {
      if (actionCount >= maxActions) {
        console.warn('AI reached max actions limit')
        return
      }

      gameState = gameEngine.getGameState()
      if (gameState.phase === 'ended' || !gameEngine.isAIPlayer(gameState.players[gameState.currentPlayerIndex].id)) {
        // Game ended or not AI's turn anymore, stop
        return
      }

      const currentPlayerId = gameState.players[gameState.currentPlayerIndex].id
      const action = ai.getNextAction(gameState, currentPlayerId)

      try {
        if (action.type === 'play-card' && action.cardId && action.position !== undefined) {
          gameEngine.playCard(currentPlayerId, action.cardId, action.position)
        } else if (action.type === 'attack-creature' && action.attackerId && action.targetId) {
          gameEngine.attack(currentPlayerId, action.attackerId, action.targetId)
        } else if (action.type === 'attack-player' && action.attackerId && action.targetPlayerId) {
          gameEngine.attack(currentPlayerId, action.attackerId, action.targetPlayerId)
        } else if (action.type === 'end-turn') {
          gameEngine.endTurn(currentPlayerId)
        }

        gameState = gameEngine.getGameState()
        setGameState(convertGameState(gameState))
        actionCount++

        // Schedule next action
        setTimeout(executeOneAction, 200)
      } catch (error) {
        console.error('AI action failed:', error)
        try {
          gameEngine.endTurn(currentPlayerId)
        } catch (e) {
          console.error('Failed to end turn:', e)
        }
      }
    }

    executeOneAction()
  }, [gameEngine, convertGameState])

  const handleEndTurn = useCallback(() => {
    if (!gameEngine || !gameState) return

    const result = gameEngine.endTurn(playerId)
    if (result.success) {
      const newState = gameEngine.getGameState()
      const oldHandSize = gameState.players[0].hand.length

      setGameState(convertGameState(newState))
      addEvent(`End turn - Turn ${newState.turn}`)

      // If hand was full, notify about overdraw
      if (oldHandSize >= MAX_HAND_SIZE && isMyTurn) {
        toast.warning('Hand is full! Card discarded.', {
          description: 'You cannot hold more than 10 cards.',
          duration: 3000
        })
      }

      setSelectedCreatureId(null)

      // After short delay, execute AI moves if it's the AI's turn
      if (gameEngine.isAIPlayer(newState.players[newState.currentPlayerIndex].id)) {
        setTimeout(() => {
          executeAITurns()
        }, 300)
      }
    }
  }, [gameEngine, gameState, playerId, addEvent, convertGameState, isMyTurn, executeAITurns])

  const handlePlayCard = useCallback(
    (cardId: string) => {
      if (!isMyTurn || !gameEngine || gameState?.phase === 'ended') return

      // Get fresh engine state to find the card
      const engineState = gameEngine.getGameState()
      const player = engineState.players[0]
      const card = player.hand.find((c) => c.id === cardId)
      if (!card) return

      // Items must be dropped on creatures, not played to board
      if (card.type === 'item') {
        toast.error('Drag items onto creatures to equip them')
        return
      }

      // Find empty position in fresh state
      const position = engineState.board.playerLane.indexOf(null)
      if (position === -1) {
        toast.error('Board is full!')
        return
      }

      const result = gameEngine.playCard(playerId, cardId, position)
      if (result.success) {
        setGameState(convertGameState(gameEngine.getGameState()))
        addEvent(`You play ${card.name}`)
      } else {
        toast.error(result.message || 'Failed to play card')
      }
    },
    [isMyTurn, gameEngine, gameState, playerId, addEvent, convertGameState],
  )

  const handleAttackCreature = useCallback(
    (targetId: string) => {
      if (!isMyTurn || !gameEngine || !gameState || !selectedCreatureId) return

      const attacker = gameState.board.playerLane.find((c) => c?.id === selectedCreatureId)
      const target = gameState.board.opponentLane.find((c) => c?.id === targetId)

      if (!attacker || !target) return

      const result = gameEngine.attack(playerId, selectedCreatureId, targetId)
      if (result.success) {
        setGameState(convertGameState(gameEngine.getGameState()))
        addEvent(`${attacker.card.name} attacks ${target.card.name}`)
        setSelectedCreatureId(null)
      } else {
        toast.error(result.message || 'Invalid attack')
      }
    },
    [isMyTurn, gameEngine, gameState, selectedCreatureId, playerId, addEvent, convertGameState],
  )

  const handleAttackPlayer = useCallback(
    (targetPlayerId: string) => {
      if (!isMyTurn || !gameEngine || !gameState || !selectedCreatureId) return

      const attacker = gameState.board.playerLane.find((c) => c?.id === selectedCreatureId)
      if (!attacker) return

      const result = gameEngine.attack(playerId, selectedCreatureId, targetPlayerId)
      if (result.success) {
        const newState = gameEngine.getGameState()
        setGameState(convertGameState(newState))
        addEvent(`${attacker.card.name} attacks for ${attacker.currentPower} damage`)
        setSelectedCreatureId(null)
      } else {
        toast.error(result.message || 'Invalid attack')
      }
    },
    [isMyTurn, gameEngine, gameState, selectedCreatureId, playerId, addEvent, convertGameState],
  )

  const handleDropOnCreature = useCallback(
    (creatureId: string, cardId: string) => {
      if (!isMyTurn || !gameEngine || !gameState) return

      const player = gameState.players[0]
      const card = player.hand.find((c) => c.id === cardId)
      if (!card) return

      const result = gameEngine.applyItemOrSpell(playerId, cardId, creatureId)
      if (result.success) {
        setGameState(convertGameState(gameEngine.getGameState()))

        const targetCreature = [
          ...gameState.board.playerLane,
          ...gameState.board.opponentLane,
        ].find((c) => c?.id === creatureId)

        if (card.type === 'item') {
          addEvent(
            `Equipped ${card.name} to ${targetCreature?.card.name || 'creature'}` +
            (card.power || card.health ? ` (+${card.power ?? 0}/+${card.health ?? 0})` : ''),
          )
        } else {
          addEvent(`Cast ${card.name} on ${targetCreature?.card.name || 'creature'}`)
        }
      } else {
        toast.error(result.message || 'Failed to apply card')
      }
    },
    [isMyTurn, gameEngine, gameState, playerId, addEvent, convertGameState],
  )


  // Timer effect: counts down each turn
  useEffect(() => {
    if (!isMyTurn || !gameEngine || gameResult) {
      setTurnTimeRemaining(turnTimerConfig)
      return
    }

    const timer = setInterval(() => {
      setTurnTimeRemaining((prev) => {
        if (prev <= 1) {
          // Time's up - forfeit turn
          handleEndTurn()
          return turnTimerConfig
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(timer)
  }, [isMyTurn, turnTimerConfig, gameEngine, gameResult, handleEndTurn])

  // Reset timer when turn changes
  useEffect(() => {
    setTurnTimeRemaining(turnTimerConfig)
  }, [gameState?.currentPlayerIndex, turnTimerConfig])

  // NEW: Global Win/Lose Detection
  useEffect(() => {
    if (gameState?.phase === 'ended' && !gameResult) {
      console.log('[WinDetection] Game ended. Winner:', gameState.winner)
      if (gameState.winner === playerId) {
        setGameResult('win')
        toast.success('Victory!', { description: 'You have defeated the opponent.' })
      } else if (gameState.winner) {
        setGameResult('lose')
        toast.error('Defeat!', { description: 'The syndicate has fallen.' })
      }
    }
  }, [gameState?.phase, gameState?.winner, gameResult, playerId])

  const inspectedCardData = useMemo(() => {
    if (!inspectedCardId) return null
    // Try board units (player)
    const boardUnit = [
      ...(gameState?.board.playerLane ?? []),
      ...(gameState?.board.opponentLane ?? []),
    ].find((c) => c?.id === inspectedCardId)

    if (boardUnit) {
      return {
        id: boardUnit.id,
        name: boardUnit.card.name,
        power: boardUnit.currentPower,
        health: boardUnit.currentHealth,
        cost: boardUnit.card.cost,
        imageUrl: boardUnit.card.imageUrl,
        faction: boardUnit.card.faction,
        type: boardUnit.card.type,
        description: boardUnit.card.description,
        keywords: boardUnit.keywords,
        equippedItems: boardUnit.equippedItems,
      }
    }

    // Try hand
    const handCard = gameState?.players[0].hand.find((c) => c.id === inspectedCardId)
    if (handCard) {
      return {
        id: handCard.id,
        name: handCard.name,
        power: handCard.power,
        health: handCard.health,
        cost: handCard.cost,
        imageUrl: handCard.imageUrl,
        faction: handCard.faction,
        type: handCard.type,
        description: handCard.description,
        keywords: handCard.keywords as Keyword[],
      }
    }

    return null
  }, [inspectedCardId, gameState])

  const playerHand = useMemo(
    () =>
      gameState?.players[0]?.hand.map((c) => ({
        id: c.id,
        name: c.name,
        type: c.type,
        cost: c.cost,
        power: c.power,
        health: c.health,
        description: c.description,
        keywords: (c.keywords || []) as Keyword[],
        imageUrl: c.imageUrl,
      })) ?? [],
    [gameState?.players[0]?.hand],
  )

  const _opponentHandCount = gameState?.players[1]?.hand.length ?? 0

  const playerCreatures = useMemo(
    () =>
      gameState?.board.playerLane.map((c) => {
        if (!c) return null
        return {
          id: c.id,
          name: c.card.name,
          power: c.currentPower,
          health: c.currentHealth,
          keywords: c.keywords,
          canAttack: c.canAttack && !c.hasAttacked,
          hasAttacked: c.hasAttacked,
          currentPower: c.currentPower,
          currentHealth: c.currentHealth,
          maxHealth: c.card.health || 1,
          card: c.card,
          imageUrl: c.card.imageUrl,
          description: c.card.description,
          equippedItems: [],
        }
      }) ?? Array(LANE_SIZE).fill(null),
    [gameState?.board.playerLane],
  )

  const opponentCreatures = useMemo(
    () =>
      gameState?.board.opponentLane.map((c) => {
        if (!c) return null
        return {
          id: c.id,
          name: c.card.name,
          power: c.currentPower,
          health: c.currentHealth,
          keywords: c.keywords,
          canAttack: c.canAttack && !c.hasAttacked,
          hasAttacked: c.hasAttacked,
          currentPower: c.currentPower,
          currentHealth: c.currentHealth,
          maxHealth: c.card.health || 1,
          card: c.card,
          imageUrl: c.card.imageUrl,
          description: c.card.description,
        }
      }) ?? Array(LANE_SIZE).fill(null),
    [gameState?.board.opponentLane],
  )



  if (appState === 'MENU') {
    return (
      <>
        <ThemeSelection
          onSelect={(theme) => {
            setSelectedTheme(theme)
            setAppState('GAME')
          }}
          onViewCatalog={() => setShowCatalog(true)}
        />
        <AnimatePresence>
          {showCatalog && (
            <CardCatalog
              selectedTheme={selectedTheme}
              onClose={() => setShowCatalog(false)}
            />
          )}
        </AnimatePresence>
      </>
    )
  }

  if (!gameState) {
    return (
      <div className="flex items-center justify-center h-screen text-base text-accent font-mono text-white/50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-2 border-white/10 border-t-white/60 animate-spin rounded-full" />
          <span className="tracking-[0.4em] uppercase text-[10px] font-black">Initializing Engine</span>
        </div>
      </div>
    )
  }

  const handleReturnToMenu = () => {
    setAppState('MENU')
    setGameEngine(null)
    setGameState(null)
    setGameResult(null)
  }

  return (
    <div className={cn('h-screen w-screen relative flex flex-col overflow-hidden font-body text-white/90 selection:bg-white selection:text-black ui-grid', `theme-${selectedTheme.toLowerCase()}`)}>
      {/* Unified Top Navigation */}
      <header className="h-14 w-full border-b border-white/10 bg-black/60 backdrop-blur-md flex items-center justify-between px-6 shrink-0 z-[110]">
        <div className="flex items-center">
          <h1 className="text-3xl font-action tracking-wider leading-none text-white uppercase mt-1 drop-shadow-lg">BUFF</h1>
        </div>

        <div className="flex items-center gap-16">
          {/* Minimalist Stats */}
          <div className={cn("flex items-center gap-6 transition-all duration-500", !isMyTurn ? "opacity-100" : "opacity-40")}>
            <div className="flex flex-col items-end">
              <span className="text-[10px] font-black text-red-500/80 uppercase tracking-[0.2em] leading-none mb-[-2px]">Opponent</span>
              <span className="text-3xl font-action tabular-nums text-red-500 leading-none tracking-wide drop-shadow-md">{gameState?.players[1]?.health} <span className="font-sans text-[10px] font-black opacity-60 tracking-normal">HP</span></span>
            </div>

            <div className="flex flex-col items-end gap-1.5 min-w-[140px]">
              <div className="flex gap-1">
                {Array.from({ length: 10 }).map((_, i) => (
                  <div
                    key={i}
                    className={cn(
                      "w-3 h-1.5 transition-all duration-500 rounded-[1px]",
                      i < (gameState?.players[1]?.magicka ?? 0)
                        ? "bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.6)]"
                        : i < (gameState?.players[1]?.maxMagicka ?? 0)
                          ? "bg-white/20"
                          : "bg-transparent border border-white/10"
                    )}
                  />
                ))}
              </div>
              <span className="text-[10px] font-black tabular-nums text-white/50 uppercase tracking-widest whitespace-nowrap">
                Energy {gameState?.players[1]?.magicka}<span className="text-white/20 mx-0.5">/</span>{gameState?.players[1]?.maxMagicka}
              </span>
            </div>
          </div>

          <div className="h-8 w-[1px] bg-white/10" />

          {/* Core Action */}
          <div className="flex flex-col items-center min-w-[120px]">
            {isMyTurn ? (
              <button
                onClick={handleEndTurn}
                className="text-[10px] font-black uppercase tracking-[0.4em] text-emerald-400 hover:text-white transition-colors"
              >
                END TURN
              </button>
            ) : (
              <span className="text-[9px] font-black uppercase tracking-[0.4em] text-white/20">WAITING</span>
            )}
          </div>

          <div className="h-8 w-[1px] bg-white/10" />

          <div className={cn("flex items-center gap-6 transition-all duration-500", isMyTurn ? "opacity-100" : "opacity-40")}>
            <div className="flex flex-col items-start gap-1.5 min-w-[140px]">
              <div className="flex gap-1">
                {Array.from({ length: 10 }).map((_, i) => (
                  <div
                    key={i}
                    className={cn(
                      "w-3 h-1.5 transition-all duration-500 rounded-[1px]",
                      i < (gameState?.players[0]?.magicka ?? 0)
                        ? "bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.6)]"
                        : i < (gameState?.players[0]?.maxMagicka ?? 0)
                          ? "bg-white/20"
                          : "bg-transparent border border-white/10"
                    )}
                  />
                ))}
              </div>
              <span className="text-[10px] font-black tabular-nums text-white/50 uppercase tracking-widest whitespace-nowrap">
                Energy {gameState?.players[0]?.magicka}<span className="text-white/20 mx-0.5">/</span>{gameState?.players[0]?.maxMagicka}
              </span>
            </div>

            <div className="flex flex-col items-start">
              <span className="text-[10px] font-black text-emerald-500/80 uppercase tracking-[0.2em] leading-none mb-[-2px]">Player</span>
              <span className="text-3xl font-action tabular-nums text-emerald-500 leading-none tracking-wide drop-shadow-md">{gameState?.players[0]?.health} <span className="font-sans text-[10px] font-black opacity-60 tracking-normal">HP</span></span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4 group w-[100px] justify-end" />
      </header>

      <div className="flex-1 flex overflow-hidden relative">
        {/* 1. Global Background Art */}
        <div className="absolute inset-0 z-[-1] overflow-hidden">
          <div className="absolute inset-0 bg-[#020617] transition-colors duration-1000" />

          <img
            key={selectedTheme}
            src={
              selectedTheme === 'FANTASY'
                ? "/backgrounds/fantasy.png"
                : selectedTheme === 'NOIR'
                  ? "/backgrounds/noir.png"
                  : "/backgrounds/sunset.png"
            }
            className="absolute inset-0 w-full h-full object-cover board-art-bg animate-in fade-in duration-1000"
            alt="background"
          />

          <div className={cn(
            "absolute inset-0 bg-gradient-to-t via-transparent to-transparent opacity-90 transition-colors duration-1000",
            selectedTheme === 'FANTASY' ? "from-[#022c22]" :
              selectedTheme === 'SUNSET' ? "from-[#2e1065]" :
                "from-black"
          )} />
        </div>

        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/noise-lines.png')] opacity-[0.03] pointer-events-none z-50 mix-blend-overlay" />

        <div className="flex-1 flex flex-col relative overflow-hidden">
          <main className="flex-1 flex flex-col p-0 relative overflow-hidden">
            <div className={cn(
              "flex-1 flex flex-col transition-all duration-1000",
              isMyTurn ? "opacity-100" : "opacity-90"
            )}>
              <GameBoard
                playerCreatures={playerCreatures}
                opponentCreatures={opponentCreatures}
                onPlayerCreatureClick={(c) => {
                  setSelectedCreatureId(c.id === selectedCreatureId ? null : c.id)
                  setInspectedCardId(c.id)
                }}
                onOpponentCreatureClick={(c) => {
                  if (selectedCreatureId) {
                    handleAttackCreature(c.id)
                  }
                  setInspectedCardId(c.id)
                }}
                selectedCreatureId={selectedCreatureId}
                dragOverSlot={dragOverSlot}
                handleDragOver={(e, pos) => {
                  e.preventDefault()
                  setDragOverSlot(pos)
                }}
                handleDragLeave={() => setDragOverSlot(null)}
                handleDrop={() => {
                  const cardId = (window as unknown as Record<string, unknown>)._draggedCardId
                  if (cardId) handlePlayCard(cardId as string)
                  setDragOverSlot(null)
                }}
                onDropOnCreature={handleDropOnCreature}
                onFaceClick={() => {
                  if (selectedCreatureId && gameState?.players[1]) {
                    handleAttackPlayer(gameState.players[1].id)
                  }
                }}
                turnTimeRemaining={turnTimeRemaining}
                turnTimerConfig={turnTimerConfig}
                isMyTurn={isMyTurn}
                selectedTheme={selectedTheme}
              />
            </div>
          </main>

          <GameFooter
            hand={playerHand}
            isPlayerTurn={isMyTurn}
            canEndTurn={true}
            onEndTurn={handleEndTurn}
            onPlayCard={handlePlayCard}
            onInspect={(c) => setInspectedCardId(c.id)}
            selectedTheme={selectedTheme}
          />
        </div>

        <AnimatePresence>
          {gameResult && (
            <GameResultOverlay
              result={gameResult}
              onReturnToMenu={handleReturnToMenu}
              selectedTheme={selectedTheme}
            />
          )}
        </AnimatePresence>

        <CardInspectorOverlay
          selectedCard={inspectedCardData}
          selectedTheme={selectedTheme}
          onClose={() => setInspectedCardId(null)}
        />

        <Toaster position="top-center" closeButton richColors />
      </div>
    </div>
  )
}

export default App
